import React, { useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import { isArray } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { IconButton, Slider } from '@mui/material'

import { apphistory, AppState } from '../../store'
import { AppLinks } from '../../environment/app-links'
import {getStepValueSorted, numberToDisplayString} from '../../common/helpers/number.helpers'
import offerService, { RecalculateOfferProps } from '../../service/OfferService'
import {IPredefinedSingleOfferResponse, IRecalculateOffert, ISubmitChooseOfferData, PREDEFINED_PERIODS_OFFER_LOGIC, SliderStep} from '../../store/types'
import { USER_CHANGE_CURRENT_LOAN_AMOUNT, RECALCULATE_OFFER, SET_CURRENT_CUSTOM_CLIENT_OFFER, CHANGE_CURRENT_INSTALLMENT_NUMBER } from '../../store/actions/offert-action'
import { getMinimumLoanAmount, monthsAmountDisplayedNames } from '../../common/helpers/helpers'
import useIsMobile from '../../hooks/useIsMobile'
import { CUSTOM_CLIENT_OFFERS_NAMES, customClientOffersConfig, selectCustomClientOfferBasedOnLoanLimits } from './customClientOffersConfig'
import { usePositiveNumberState } from './hooks/usePositiveNumberState'
import { ICustomClientOfferDefaultOfferSummaryProps, ICustomClientOfferPageMainContentProps, ICustomClientOfferPageSliderProps, ICustomClientOfferPredefinedPeriodOfferSummaryProps } from './CustomClientOfferPageProps'
import { invokeLoadingWithText } from '../../common/invokeLoading'
import useTextContents from '../../hooks/useTextContents'
import analyticsService from '../../service/analytics.service'
import { PredefinedOfferDetailsModal } from '../PredefinedOfferPage/PredefinedOfferPage'
import PageScreen from "../../components/organisms/Page/PageScreen";
import AppButton from "../../components/atoms/AppButton/AppButton";
import {UI_BUTTON_TYPE} from "../../components/atoms/AppButton/AppButtonProps";
import InlineSpinner from "../../components/atoms/InlineSpinner/InlineSpinner";
import Paragraph from '../../components/atoms/Paragraph/Paragraph'
import PredefinedPeriodButtons from '../../components/molecules/PredefinedPeriodButtons/PredefinedPeriodButtons'
import { Environment } from '../../environment/app-settings'
import { SET_APP_HAS_ERROR, SET_CURRENT_SELECTED_PREDEFINED_PERIOD } from '../../store/actions/application-actions'

import SubtractIcon from '../../icons/svg/minus-icon.svg'
import AddIcon from '../../icons/svg/add-icon.svg'
import './CustomClientOfferPageStyles.scss';




/**
 * [ZAŁOŻENIE BIZNESOWE] - MECHANIZM PREDEFINIOWANYCH OKRESÓW POŻYCZKI
*/
const CustomClientOfferDefaultOfferSummary = (props: ICustomClientOfferDefaultOfferSummaryProps) => {

   const { isLoading, isDebouncing, instalmentAmount, currentSelectedInstallmentNumber, textContent } = props

   return (
      <div className='custom-client-offer-page-slider-default-summary-wrapper'>
         <div className='custom-client-offer-page-slider-summary-item-wrapper' style={{ borderRight: 'solid 1px var(--main-aside-text-color)'}}>
            <p>{textContent('customClientOfferPage.sliderSummaryInstallmentsAmountHeading')}</p>
            {
               (isLoading || isDebouncing) ? <InlineSpinner size={19} /> : <h6>{numberToDisplayString(instalmentAmount, " zł")}</h6>
            }
         </div>
         <div className='custom-client-offer-page-slider-summary-item-wrapper'>
            <p>{props.textContent('customClientOfferPage.sliderSummaryInstallmentsHeading')} </p>
            <h6>{monthsAmountDisplayedNames(currentSelectedInstallmentNumber ?? 0)}</h6>
         </div>
      </div>
   )
}

const CustomClientOfferPredefinedPeriodOfferSummary = (props: ICustomClientOfferPredefinedPeriodOfferSummaryProps) => {
   const dispatch = useDispatch()
   const { isLoading, isDebouncing, instalmentAmount, currentSelectedPredefinedPeriod, textContent, handleRecalculateOfferOnPeriodChange } = props

   const [currentSelectedPeriod, setCurrentSelectedPeriod] = useState<PREDEFINED_PERIODS_OFFER_LOGIC | undefined>(currentSelectedPredefinedPeriod)
   const [isContentLoading, setIsContentLoading] = useState<boolean>(false)

   useEffect(() => {
      setCurrentSelectedPeriod(currentSelectedPredefinedPeriod)
   }, [currentSelectedPredefinedPeriod])


   const handleAdditionalActionOnButtonClick = useCallback(async(newSelectedPeriod: PREDEFINED_PERIODS_OFFER_LOGIC) => {
      dispatch({ // ZMIENIAMY OBECNIE WYBRANY OKRES SPŁATY, PONIEWAŻ UŻYWAMY GO DO PRZELICZEŃ I PREZENTACJI WARTOŚCI
         type: CHANGE_CURRENT_INSTALLMENT_NUMBER,
         payload: Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[newSelectedPeriod]
      })

      await handleRecalculateOfferOnPeriodChange(newSelectedPeriod)
   }, [ handleRecalculateOfferOnPeriodChange, dispatch ])

   return (
      <div className='custom-client-offer-page-slider-predefined-period-summary-wrapper'>
         <div className='custom-client-offer-page-slider-summary-item-wrapper'>
            <p>{textContent('customClientOfferPage.sliderSummaryInstallmentsAmountHeading')}</p>
            {
               (isLoading || isDebouncing) ? <InlineSpinner size={19} /> : <h6>{numberToDisplayString(instalmentAmount, " zł")}</h6>
            }
         </div>
         <Paragraph bold align='center'>Wybierz okres spłaty:</Paragraph>
         <PredefinedPeriodButtons
            currentSelectedPeriod={currentSelectedPeriod} setCurrentSelectedPeriod={setCurrentSelectedPeriod}  
            isLoading={isContentLoading} setIsButtonLoading={setIsContentLoading} handleAdditionalActionOnButtonClick={handleAdditionalActionOnButtonClick}
         />
      </div>
   )
}


const CustomClientOfferPageSlider = (props: ICustomClientOfferPageSliderProps ) => {
   const dispatch = useDispatch()
   const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
   const DEFAULT_MIN_DEBOUNCE_TIMEOUT = 250
   const DEFAULT_MAX_DEBOUNCE_TIMEOUT = 1000
   const DEFAULT_STEP_DEBOUNCE_TIMEOUT = 150
   const [isDebouncing, setIsDebouncing] = useState(false);

   const [sliderValue, setSliderValue] = usePositiveNumberState(props.requestedAmount, props.minimumLoanAmount);
   const snappedRef = useRef<number>(sliderValue);
   const lastRawRef = useRef<number>(sliderValue);

   const [stepUp, setStepUp] = useState<number>(()=>{
      return  getStepValueSorted(props.defaultOfferSliderSteps, sliderValue);
   })

   const [stepDown, setStepDown] = useState<number>(()=>{
      return  getStepValueSorted(props.defaultOfferSliderSteps, sliderValue-stepUp);
   })

   const marks = [
      {value: props.minimumLoanAmount, label: numberToDisplayString(props.minimumLoanAmount)},
      {value: props.currentMaximumLoanAmount, label: numberToDisplayString(props.currentMaximumLoanAmount)},
   ]

   useEffect(() => {
      setSliderValue(props.requestedAmount)
   }, [props.requestedAmount])

   useEffect(() => {
      recalculateOfferOnDemand()
      setIsDebouncing(false)
   }, [props.submitSignal]);

   useEffect(() => {
      let stepUp=  getStepValueSorted(props.defaultOfferSliderSteps, sliderValue);
      if(sliderValue + stepUp > props.currentMaximumLoanAmount){
         stepUp = (props.currentMaximumLoanAmount - sliderValue)
      }
      let stepDown = getStepValueSorted(props.defaultOfferSliderSteps, sliderValue-stepUp);
      if(sliderValue=== props.currentMaximumLoanAmount){
         if(sliderValue%stepDown){
            stepDown = sliderValue%stepDown;
         }
      }else if(sliderValue - stepDown < props.minimumLoanAmount){
            stepDown = (sliderValue-props.minimumLoanAmount)
      }

      setStepUp(stepUp);
      setStepDown(stepDown);
      dispatch({
         type: SET_CURRENT_CUSTOM_CLIENT_OFFER,
         payload: selectCustomClientOfferBasedOnLoanLimits(sliderValue, props.minimumLoanAmount, props.currentMaximumLoanAmount)
      })
   }, [sliderValue])

   useEffect(() => {
      return () => {
         if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
            setIsDebouncing(false)
         }
      }
   }, [])

   const noOfRecalculation = useSelector(
       (state: AppState) => state.offertState.noOfRecalculation
   );

   const handleOnSliderControlButtonClick = async(actionType: 'add' | 'subtract') => {
      let newSliderValue = 0

      if (actionType === 'add') {
         newSliderValue = sliderValue + stepUp
      } 
      else if (actionType === 'subtract') {
         newSliderValue = sliderValue - stepDown
      }

      if (newSliderValue < props.minimumLoanAmount){
         newSliderValue = props.minimumLoanAmount;
      } 
      else if(newSliderValue > props.currentMaximumLoanAmount){
         newSliderValue = props.currentMaximumLoanAmount;
      }
      else {
         setSliderValue(newSliderValue)
      }
      if (debounceTimeoutRef.current) {
         clearTimeout(debounceTimeoutRef.current)
      }
      const delay = Math.min(
          DEFAULT_MAX_DEBOUNCE_TIMEOUT,
          DEFAULT_MIN_DEBOUNCE_TIMEOUT + (noOfRecalculation - 1) * DEFAULT_STEP_DEBOUNCE_TIMEOUT
      );
      setIsDebouncing(true)

      debounceTimeoutRef.current = setTimeout(async () => {
         props.setCurrentLoanAmountWithCheck(newSliderValue, setSliderValue)
         setIsDebouncing(false)
      }, delay)
   }

   const clamp = (v: number) => Math.min(Number(props.currentMaximumLoanAmount), Math.max(Number(props.minimumLoanAmount), v));
   useEffect(() => {
      snappedRef.current = sliderValue;
      lastRawRef.current = sliderValue;
   }, [sliderValue]);

   const handleChange = (_: any, newValue: number | number[]) => {
      const raw = Array.isArray(newValue) ? newValue[0] : newValue;

      const min = Number(props.minimumLoanAmount);
      const max = Number(props.currentMaximumLoanAmount);

      const safeStepUp = Math.max(1, Number(stepUp));
      const safeStepDown = Math.max(1, Number(stepDown));

      const lastRaw = lastRawRef.current;
      const dirUp = raw > lastRaw;

      let snapped = snappedRef.current;

      const alignUp = (v: number) => {
         if (v <= min) return min;
         if (v >= max) return max;
         const aligned = Math.ceil(v / safeStepUp) * safeStepUp;
         return clamp(aligned);
      };

      const alignDown = (v: number) => {
         if (v <= min) return min;
         if (v >= max) return max;
         const aligned = Math.floor(v / safeStepDown) * safeStepDown;
         return clamp(aligned);
      };

      if (dirUp) {
         snapped = alignUp(snapped);
      } else {
         snapped = alignDown(snapped);
      }

      const stepDir = dirUp ? safeStepUp : safeStepDown;

      if (dirUp) {
         const k = Math.floor((raw - snapped) / stepDir);
         if (k >= 1) snapped = snapped + k * stepDir;
      } else {
         const k = Math.floor((snapped - raw) / stepDir);
         if (k >= 1) snapped = snapped - k * stepDir;
      }

      if (raw >= max) snapped = max;
      if (raw <= min) snapped = min;

      snapped = clamp(snapped);
      lastRawRef.current = raw;

      const final = snapToFinalStepWithDir(snapped, dirUp);

      if (final !== snappedRef.current) {
         snappedRef.current = snapped;
         setSliderValue(final);
      }
   };

   const snapToFinalStepWithDir = (v: number, dirUp: boolean) => {
      const vv = clamp(v);
      if (vv === Number(props.minimumLoanAmount) || vv === Number(props.currentMaximumLoanAmount)) return vv;

      const base = Number(props.minimumLoanAmount);
      const stepFinal = getStepValueSorted(props.defaultOfferSliderSteps, vv);
      if (!stepFinal || stepFinal <= 0) return vv;

      const k = (vv - base) / stepFinal;
      const kk = dirUp ? Math.ceil(k) : Math.floor(k);

      return clamp(base + kk * stepFinal);
   };

   const handleCommitted = () => {
      if(!isArray(sliderValue)){
         props.setCurrentLoanAmountWithCheck(sliderValue, setSliderValue)
      }
   };

   const recalculateOfferOnDemand = () =>{
      if (debounceTimeoutRef.current) {
         clearTimeout(debounceTimeoutRef.current)
      }
      props.setCurrentLoanAmountWithCheck(sliderValue, setSliderValue)
   }

   return (
      <div>
         <div className='custom-client-offer-page-slider-buttons-wrapper'>
            <p className='custom-client-offer-page-slider-buttons-heading'>Kwota</p>
            <IconButton classes={{ root: 'custom-client-offer-page-slider-button'}} id="custom-client-offer-page-slider-button-subtract" onClick={() => handleOnSliderControlButtonClick('subtract')}>
               <img src={SubtractIcon} alt="-" />
            </IconButton>
            <h4 className='custom-client-offer-page-slider-buttons-amount'>{numberToDisplayString(sliderValue, " zł")}</h4>
            <IconButton classes={{ root: 'custom-client-offer-page-slider-button'}} id="custom-client-offer-page-slider-button-add" onClick={() => handleOnSliderControlButtonClick('add')}>
               <img src={AddIcon} alt="+" />
            </IconButton>
         </div>
         <Slider
            sx={{
               WebkitTapHighlightColor: "transparent",
               WebkitUserSelect: "none",
               WebkitTouchCallout: "none",
               touchAction: "pan-x",

               "& .MuiSlider-thumb": {
                  WebkitTapHighlightColor: "transparent",
                  WebkitUserSelect: "none",
                  WebkitTouchCallout: "none",
               },
            }}
            value={sliderValue}
            min={props.minimumLoanAmount}
            max={props.currentMaximumLoanAmount}
            step={Math.max(1, Math.min(stepUp, stepDown))}
            marks={marks}
            onChange={handleChange}
            onChangeCommitted={handleCommitted}
            classes={{
               root: 'custom-client-offer-page-slider-root', 
               thumb: 'custom-client-offer-page-slider-thumb', 
               track: 'custom-client-offer-page-slider-track', 
               rail: 'custom-client-offer-page-slider-rail',
               mark: 'custom-client-offer-page-slider-mark',
               markLabel: 'custom-client-offer-page-slider-mark-label',
            }}
         />
         {
            props.IS_PREDEFINED_PERIODS_AVAILABLE ?  // [ZAŁOŻENIE BIZNESOWE] - MECHANIZM PREDEFINIOWANYCH OKRESÓW POŻYCZKI
            (
               <CustomClientOfferPredefinedPeriodOfferSummary 
                  textContent={props.textContent}
                  isDebouncing={isDebouncing}
                  isLoading={props.isLoading}
                  instalmentAmount={props.instalmentAmount}
                  currentSelectedPredefinedPeriod={props.currentSelectedPredefinedPeriod}
                  handleRecalculateOfferOnPeriodChange={props.handleRecalculateOfferOnPeriodChange}
               /> 
            ) :
            (
               <CustomClientOfferDefaultOfferSummary 
                  textContent={props.textContent}
                  isDebouncing={isDebouncing}
                  isLoading={props.isLoading}
                  instalmentAmount={props.instalmentAmount}
                  currentSelectedInstallmentNumber={props.currentSelectedInstallmentNumber}
               />
            )
         }
      </div>
   )
}


const CustomClientOfferPageMainContent = (props: ICustomClientOfferPageMainContentProps) => {
   const  dispatch = useDispatch()
   const { offer, IS_PREDEFINED_PERIODS_AVAILABLE, textContent, handleRecalculateOffer } = props
   const currentSelectedUserOffer = customClientOffersConfig[offer.currentCustomClientOffer !== null ? offer.currentCustomClientOffer : CUSTOM_CLIENT_OFFERS_NAMES.LOWEST_OFFER]
   const abortRef = useRef<AbortController | null>(null);
   const shortPeriodMaximumLoanAmount = offer.maximumLoanAmount_minimumInstallment ?? Environment.maxShortLoanPeriodRequestedAmount
   const maximumSelectableLoanAmount = IS_PREDEFINED_PERIODS_AVAILABLE === true
      ? offer.maximumLoanAmount_maximumInstallment
      : offer.currentMaximumLoanAmount

   const recalculateOffer = (recalculateOfferProps: RecalculateOfferProps) =>{
      if (abortRef.current) {
         abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;
      return handleRecalculateOffer(recalculateOfferProps, controller.signal)
   }

   const setCurrentLoanAmountWithCheck = async(newValue: number, setState: any) => {
      if(newValue === offer.requestedAmount){ //Sytuacja gdzie kwota się nie zmieniła
         return
      }

      if(isNaN(newValue)){ //Sytuacja w której z jakiegoś powodu przyszła błędna wartość
         setState(offer.requestedAmount)
         return
      }

      const recalculateOfferParams: RecalculateOfferProps = {
         offerId: offer.offerId,
         instalments: offer.currentSelectedInstallmentNumber,
         requestedAmount: 0,
      }

      if(newValue > maximumSelectableLoanAmount){
         recalculateOfferParams.requestedAmount = maximumSelectableLoanAmount
         
         setState(maximumSelectableLoanAmount)

         dispatch({
            type: USER_CHANGE_CURRENT_LOAN_AMOUNT,
            payload: maximumSelectableLoanAmount
         })
      }
      else if(newValue < offer.minimumLoanAmount){
         recalculateOfferParams.requestedAmount = offer.minimumLoanAmount

         dispatch({
            type: USER_CHANGE_CURRENT_LOAN_AMOUNT,
            payload: offer.minimumLoanAmount
         })
         
         setState(offer.minimumLoanAmount)

      }
      else {
         recalculateOfferParams.requestedAmount = newValue

         dispatch({
            type: USER_CHANGE_CURRENT_LOAN_AMOUNT,
            payload:newValue
         })
         setState(newValue)
      }

      const shouldSwitchToLongPeriod = IS_PREDEFINED_PERIODS_AVAILABLE === true
         && props.currentSelectedPredefinedPeriod === PREDEFINED_PERIODS_OFFER_LOGIC.SHORT_PERIOD
         && recalculateOfferParams.requestedAmount > shortPeriodMaximumLoanAmount

      if(shouldSwitchToLongPeriod) {
         recalculateOfferParams.instalments = Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[PREDEFINED_PERIODS_OFFER_LOGIC.LONG_PERIOD]

         dispatch({
            type: CHANGE_CURRENT_INSTALLMENT_NUMBER,
            payload: recalculateOfferParams.instalments
         })

         dispatch({
            type: SET_CURRENT_SELECTED_PREDEFINED_PERIOD,
            payload: PREDEFINED_PERIODS_OFFER_LOGIC.LONG_PERIOD
         })
      }

      await recalculateOffer(recalculateOfferParams)
   }

      
   const handleRecalculateOfferOnPeriodChange = async (selectedPeriod: PREDEFINED_PERIODS_OFFER_LOGIC) => {
      if (abortRef.current) {
         abortRef.current.abort()
      }

      const installments = Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[selectedPeriod]
      const requestedAmount = selectedPeriod === PREDEFINED_PERIODS_OFFER_LOGIC.SHORT_PERIOD
         ? Math.min(offer.requestedAmount, shortPeriodMaximumLoanAmount)
         : offer.requestedAmount
      const controller = new AbortController()
      abortRef.current = controller

      if(requestedAmount !== offer.requestedAmount) {
         dispatch({
            type: USER_CHANGE_CURRENT_LOAN_AMOUNT,
            payload: requestedAmount
         })
      }

      const recalculateOfferParams: RecalculateOfferProps = {
         offerId: offer.offerId,
         requestedAmount,
         instalments: installments,
      }
   
      await props.handleRecalculateOffer(recalculateOfferParams, controller.signal)
   }


   return (
      <div className='custom-client-offer-page-main-content-outer-wrapper'>
         <div className='custom-client-offer-page-main-content-icon-text-wrapper' key={currentSelectedUserOffer.offerId}>
            <img src={currentSelectedUserOffer.offerImageSrc} alt="icon"/>
            <h4>{textContent(currentSelectedUserOffer.headingTextPath)}</h4>
            <p>{textContent(currentSelectedUserOffer.descriptionTextPath)}</p>
         </div>
         <div>
            <CustomClientOfferPageSlider 
               {...offer}
               currentMaximumLoanAmount={maximumSelectableLoanAmount}
               textContent={textContent}
               isLoading={props.isLoading}
               submitSignal={props.submitSignal}
               setCurrentLoanAmountWithCheck={setCurrentLoanAmountWithCheck}
               handleRecalculateOfferOnPeriodChange={handleRecalculateOfferOnPeriodChange}
               defaultOfferSliderSteps={[{from: 0, value: 50}, {from: 10000, value: 100}, {from: 15000, value: 500}]}
               IS_PREDEFINED_PERIODS_AVAILABLE={IS_PREDEFINED_PERIODS_AVAILABLE}
               currentSelectedPredefinedPeriod={props.currentSelectedPredefinedPeriod}
            />
         </div>
      </div>
   )
}


const CustomClientOfferPage = () => {
   const textContent = useTextContents()
   const dispatch = useDispatch()
   const isMobileDevice = useIsMobile()
   const [isLoading, setIsLoading] = useState<boolean>(false)
   const { IS_PREDEFINED_PERIODS_AVAILABLE, currentSelectedPredefinedPeriod } = useSelector((state: AppState) => state.applicationState)

   const offer = useSelector((state: AppState) => state.offertState)
   const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.lenConfigState.analyticsEventTypesNames)
   const {predefinedUserOffersArray, predefinedUserOffersObject, ...currentUserOffer} = useSelector((state: AppState) => state.offertState)
   const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
   const [submitSignal, setSubmitSignal] = useState<number>(0)
   const handleToggleMenuOpen =  useCallback(() => setIsMenuOpen(!isMenuOpen), [isMenuOpen])


   useEffect(() => { // Przed pokazaniem użytkownikowi slidera, wykonujemy przeliczenie oferty dla aktualnych parametrów
      const shortPeriodMaximumLoanAmount = offer.maximumLoanAmount_minimumInstallment ?? Environment.maxShortLoanPeriodRequestedAmount
      const selectedInstallmentNumber = IS_PREDEFINED_PERIODS_AVAILABLE === true && currentSelectedPredefinedPeriod !== undefined
         ? Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[currentSelectedPredefinedPeriod]
         : offer.currentSelectedInstallmentNumber
      const requestedAmount = currentSelectedPredefinedPeriod === PREDEFINED_PERIODS_OFFER_LOGIC.SHORT_PERIOD
         ? Math.min(offer.requestedAmount, shortPeriodMaximumLoanAmount)
         : offer.requestedAmount

      if(selectedInstallmentNumber !== offer.currentSelectedInstallmentNumber) {
         dispatch({
            type: CHANGE_CURRENT_INSTALLMENT_NUMBER,
            payload: selectedInstallmentNumber
         })
      }

      if(requestedAmount !== offer.requestedAmount) {
         dispatch({
            type: USER_CHANGE_CURRENT_LOAN_AMOUNT,
            payload: requestedAmount
         })
      }

      const recalculateOfferParams: RecalculateOfferProps = {
         offerId: offer.offerId,
         instalments: selectedInstallmentNumber,
         requestedAmount
      }

      const initializeOffer = async () => {
         try {
            const isOk = await handleRecalculateOffer(recalculateOfferParams)
            if (!isOk) {
               dispatch({ type: SET_APP_HAS_ERROR })
            }
         } catch (error) {
            setIsLoading(false)
            dispatch({ type: SET_APP_HAS_ERROR })
         }
      }

      initializeOffer()
   }, [])


   const handleRecalculateOffer = async(recalculateOfferParams: RecalculateOfferProps, signal?: AbortSignal) => {
      setIsLoading(true);
      const response: IRecalculateOffert = await offerService.recalculatePredefinedOffer(recalculateOfferParams, signal)
      dispatch({
         type: RECALCULATE_OFFER,
         payload: response
      })

      dispatch({
         type: SET_CURRENT_CUSTOM_CLIENT_OFFER,
         payload: selectCustomClientOfferBasedOnLoanLimits(response.requestedAmount, getMinimumLoanAmount(response.minimumLoanAmount as number), response.maximumLoanAmount)
      })
      setIsLoading(false);
      return response.isOk
   }

   const handleSubmitPredefinedOffer = async() => {
      const offerToSend: ISubmitChooseOfferData = { //Mapowanie oferty trzymanej po stronie Frontendu na model z Backendu
         offerId: offer.offerId,
         instalments: offer.currentSelectedInstallmentNumber,
         requestedAmount: offer.requestedAmount,
         instalmentsAmount: offer.instalmentAmount,
      }

      dispatch(invokeLoadingWithText(textContent('loaders.prepareAgreement')))


      // Przed wybraniem umowy robimy jeszcze jedno przeliczenie, żeby zaktualizować ofertę na mongo
      const recalculateOfferResponse = await offerService.recalculateOffer({
         offerId: offer.offerId,
         instalments: offer.currentSelectedInstallmentNumber ?? 0,
         requestedAmount: offer.requestedAmount,
      })

      // Aktualizujemy  stan ostatecznym przeliczeniem oferty         
      dispatch({
         type: RECALCULATE_OFFER,
         payload: recalculateOfferResponse,
      })

      
      // =================== ZAPIS EVENTU ANALITYCZNEGO Z WYBOREM OFERTY ===================
         let additionalAgreementData: Record<string, any> | null = null


         if(IS_PREDEFINED_PERIODS_AVAILABLE === true && predefinedUserOffersObject !== undefined) {
            additionalAgreementData = {
               userRequestedAmount: currentUserOffer.requestedAmount,
               isPredefinedPeriodsLogicAvailable: true,
               recommendedOffer: {
                  description: "Mechanizm predefiniowanych okresów spłaty, nie posiada rekomendowanych ofert"
               },
               selectedOffer: {
                  description: "Szczegóły oferty wybranej przez użytkownika",
                  selectedOfferSource: "KALKULATOR OFERTY",
                  offerId: offer.offerId,
                  requestedAmount:  offer.requestedAmount,
                  instalments: offer.currentSelectedInstallmentNumber,
                  instalmentsAmount: offer.instalmentAmount,
                  ckdz: offer.ckdz,
                  interestRate: offer.interestRate,
               },
               predefinedUserOffersArray: [
                  ...predefinedUserOffersObject[PREDEFINED_PERIODS_OFFER_LOGIC.LONG_PERIOD], 
                  ...predefinedUserOffersObject[PREDEFINED_PERIODS_OFFER_LOGIC.SHORT_PERIOD],
               ]
            }
         }
         else if (IS_PREDEFINED_PERIODS_AVAILABLE === false && predefinedUserOffersArray !== undefined ) {
            additionalAgreementData = {
               userRequestedAmount: currentUserOffer.requestedAmount,
               isPredefinedPeriodsLogicAvailable: false,
               recommendedOffer: {
                  description: "Szczegóły oferty rekomendowanej",
                  ...predefinedUserOffersArray.find(singleOffer => singleOffer.isRecommended === true)
               },
               selectedOffer: {
                  description: "Szczegóły oferty wybranej przez użytkownika",
                  selectedOfferSource: "KALKULATOR OFERTY",
                  offerId: offer.offerId,
                  requestedAmount:  offer.requestedAmount,
                  instalments: offer.currentSelectedInstallmentNumber,
                  instalmentsAmount: offer.instalmentAmount,
                  ckdz: offer.ckdz,
                  interestRate: offer.interestRate,
               },
               predefinedUserOffersArray: predefinedUserOffersArray.map(singleOffer => {
                  return {
                     id: singleOffer.id,
                     name: singleOffer.name,
                     requestedAmount: singleOffer.requestedAmount,
                     instalments: singleOffer.instalments,
                     instalmentsAmount: singleOffer.instalmentsAmount,
                     isRecommended: singleOffer.isRecommended
                  }
               }).sort((a,b) => a.requestedAmount - b.requestedAmount), //Sortowanie rosnąco po kwocie oferty,
            }
         }

         if(additionalAgreementData !== null) {
            await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.UserSelectOffer, JSON.stringify(additionalAgreementData, undefined, 3))
         }
      // ================================================================================


      await offerService.chooseOffer(offerToSend)
   }

   const handleChoosingOffer =()=>{
      setSubmitSignal(submitSignal + 1)
      setIsMenuOpen(true)
   }

   return (
      <PageScreen
          title={{title: textContent("customClientOfferPage.heading"), line:true}}
          {...(offer.isCustomOfferOnly
             ? { showBackButton: false as const }
             : { showBackButton: true as const, onClickBack: () => apphistory.push(AppLinks.offert) })}
      >
         <div className='custom-client-offer-page-container' id="custom-client-offer-page-container">
            <CustomClientOfferPageMainContent
                handleRecalculateOffer={handleRecalculateOffer} isLoading={isLoading} 
                IS_PREDEFINED_PERIODS_AVAILABLE={IS_PREDEFINED_PERIODS_AVAILABLE} 
                currentSelectedPredefinedPeriod={currentSelectedPredefinedPeriod}
                offer={offer} textContent={textContent} submitSignal={submitSignal}
            />
            <AppButton
               type='submit'
               id="form-submit-button"
               onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleChoosingOffer()}
               uiButtonType={UI_BUTTON_TYPE.PRIMARY}>
               {textContent("buttons.submit")}
            </AppButton>
            <PredefinedOfferDetailsModal
               isMenuOpen={isMenuOpen}
               isLoading={isLoading}
               setIsMenuOpen={handleToggleMenuOpen}
               handleOnDialogConfirm={handleSubmitPredefinedOffer}
               textContent={textContent}
               instalmentsAmount={offer.instalmentAmount as number}
               instalments={offer.currentSelectedInstallmentNumber as number}
               interestRate={offer.interestRate as number}
               ckdz={offer.ckdz as number}
               paymentDate={offer.paymentDate as string}
               requestedAmount={offer.requestedAmount}
            />
         </div>
      </PageScreen>
   )
}

export default CustomClientOfferPage